import React, {useState} from 'react';
import './App.css';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Dashboard from './Dashboard';
import {BaseForm, EMPTY_BASE} from "./base/BaseForm";
import Wizard from "./Wizard";
import {createTheme, ThemeProvider} from '@mui/material/styles';
import Configuration from './configuration.json';

import {useTolgee, useTranslate} from "@tolgee/react";

import {
    getDebug,
    getInitialData,
    getLang,
    LOCAL_STORE_KEY_BASE,
    LOCAL_STORE_KEY_COMMENT,
    LOCAL_STORE_KEY_DEGREES,
    LOCAL_STORE_KEY_FINAL_PROJECT,
    LOCAL_STORE_KEY_LANGUAGES,
    LOCAL_STORE_KEY_MAPPINGS,
    LOCAL_STORE_KEY_MODULES, LOCAL_STORE_KEY_SHOW_DIALOGS,
    LOCAL_STORE_KEY_START_TIME,
    LOCAL_STORE_KEY_WIZARD_ACTIVE_STEP
} from "./Data";
import {AllStates, ReactState, StepForm} from "./Forms";
import {EMPTY_LANGUAGES, LanguagesForm} from "./languages/LanguagesForm";
import {DegreesForm, EMPTY_DEGREES} from "./degrees/DegreesForm";
import {EMPTY_MODULES, ModulesForm} from "./modules/ModulesForm";
import {EMPTY_MAPPINGS, MappingsForm} from "./mappings/MappingsForm";
import {EMPTY_FINAL_PROJECT, FinalProjectForm} from "./thesis/ThesisForm";
import {CommentForm, EMPTY_COMMENT} from "./comment/CommentForm";
import FromMarkdown, {FORM_DATE, FORM_NAME, FORM_VERSION, SMALL_WINDOW_WIDTH} from "./Representation";
import {
    Alert,
    AppBar,
    ButtonGroup,
    CardActionArea,
    CardMedia,
    Container, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider,
    Fab,
    Fade, Modal,
    Paper,
    Snackbar,
    SnackbarCloseReason, TextField,
    Toolbar,
    Typography,
    useScrollTrigger
} from "@mui/material";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TranslateIcon from '@mui/icons-material/Translate';

import {Base} from "./base/Base";
import {Languages} from "./languages/Languages";
import {Degrees} from "./degrees/Degrees";
import {ModuleList} from "./modules/Modules";
import {Mapping} from "./mappings/Mappings";
import {FinalProject} from "./thesis/Thesis";
import {Comment} from "./comment/Comment";

import prefillBi from "./prefill/business-informatics.json";
import prefillCs from "./prefill/computer-science.json";
import prefillDs from "./prefill/data-science.json";

import {ConfirmationDialog} from "./ConfirmationDialog";
import {ErrorBoundary} from "react-error-boundary";
import {BI_KEY, CS_KEY, DS_KEY, MarburgBachelorDialog} from "./MarburgBachelorDialog";
import {missingKeys, testMode} from "./index";

interface ScrollTopProps {
    children?: React.ReactElement<unknown>;
}

function ScrollTop({children}: ScrollTopProps) {
    const trigger = useScrollTrigger({
        target: window ? window : undefined,
        disableHysteresis: true,
        threshold: 100,
    });

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const anchor = (
            (event.target as HTMLDivElement).ownerDocument || document
        ).querySelector('#back-to-top-anchor');

        if (anchor) {
            anchor.scrollIntoView({
                block: 'start',
            });
        }
    };

    return (
        <Fade in={trigger}>
            <Box
                onClick={handleClick}
                role="presentation"
                sx={{position: 'fixed', bottom: 16, right: 16}}
            >
                {children}
            </Box>
        </Fade>
    );
}

function useFormStates<T>(useStateLocalStorage: <T>(localStoreKey: string, emptyData: T) => ReactState<T>) {
    const SHOW_ALL_DIALOGS = [true, false, false, true, true, false, false];

    const base = useStateLocalStorage(LOCAL_STORE_KEY_BASE, EMPTY_BASE);
    const languages = useStateLocalStorage(LOCAL_STORE_KEY_LANGUAGES, EMPTY_LANGUAGES);
    const degrees = useStateLocalStorage(LOCAL_STORE_KEY_DEGREES, EMPTY_DEGREES);
    const modules = useStateLocalStorage(LOCAL_STORE_KEY_MODULES, EMPTY_MODULES);
    const mappings = useStateLocalStorage(LOCAL_STORE_KEY_MAPPINGS, EMPTY_MAPPINGS);
    const finalProject = useStateLocalStorage(LOCAL_STORE_KEY_FINAL_PROJECT, EMPTY_FINAL_PROJECT);
    const comment = useStateLocalStorage(LOCAL_STORE_KEY_COMMENT, EMPTY_COMMENT);
    const startTime = useStateLocalStorage(LOCAL_STORE_KEY_START_TIME, Date.now());
    const showStepDialogs = useStateLocalStorage(LOCAL_STORE_KEY_SHOW_DIALOGS, SHOW_ALL_DIALOGS);
    const stepDialogsOpen = React.useState(showStepDialogs[0]);

    const resetShowDialogs = () => {
        showStepDialogs[1](SHOW_ALL_DIALOGS);
        stepDialogsOpen[1](SHOW_ALL_DIALOGS);
    }

    const allStates: AllStates =
        {
            base: base,
            languages: languages,
            degrees: degrees,
            modules: modules,
            mappings: mappings,
            finalProject: finalProject,
            comment: comment,
            startTime: startTime,
            ordered: [
                base,
                languages,
                degrees,
                modules,
                mappings,
                finalProject,
                comment
            ],
            showStepDialogs: showStepDialogs,
            stepsDialogsOpen: stepDialogsOpen
        };

    const stepForms: StepForm[] = [
        new BaseForm(),
        new LanguagesForm(),
        new DegreesForm(),
        new ModulesForm(),
        new MappingsForm(),
        new FinalProjectForm(),
        new CommentForm()
    ];

    return {allStates: allStates, stepForms: stepForms, resetShowDialogs: resetShowDialogs};
}

function App() {
    const useStateLocalStorage = <T, >(localStoreKey: string, emptyData: T): ReactState<T> => {
        const [data, setData] = React.useState(getInitialData(localStoreKey, emptyData));
        localStorage.setItem(localStoreKey, JSON.stringify(data));
        return [data, setData];
    }

    const {t} = useTranslate();
    const tolgee = useTolgee();
    const lang = getLang();
    const debug = getDebug();

    const [wizardActiveStep, setWizardActiveStep]: ReactState<number> = useStateLocalStorage(LOCAL_STORE_KEY_WIZARD_ACTIVE_STEP, 0);
    const [resetConfirmationOpen, setResetConfirmationOpen] = React.useState(false);
    const [prefillConfirmationOpen, setPrefillConfirmationOpen] = React.useState(false);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<"success" | "info" | "warning" | "error">("info");
    const [snackbarMessage, setSnackbarMessage] = React.useState("");
    const [reportErrorDialogOpen, setReportErrorDialogOpen] = React.useState(false);
    const [debugText, setDebugText] = React.useState("");

    const {allStates: allStates, stepForms: stepForms, resetShowDialogs: resetShowDialogs} = useFormStates(useStateLocalStorage);

    const reset = () => {
        allStates.base[1](EMPTY_BASE);
        allStates.languages[1](EMPTY_LANGUAGES);
        allStates.degrees[1](EMPTY_DEGREES);
        allStates.modules[1](EMPTY_MODULES);
        allStates.mappings[1](EMPTY_MAPPINGS);
        allStates.finalProject[1](EMPTY_FINAL_PROJECT);
        allStates.comment[1](EMPTY_COMMENT);
        allStates.startTime[1](Date.now());
        setWizardActiveStep(0);
    };

    const prefillWith = (prefillData: {
        degrees: Degrees,
        modules: ModuleList[],
        finalProject: FinalProject,
        mappings: Mapping[]
    }) => {
        allStates.degrees[1](prefillData.degrees);
        allStates.modules[1](prefillData.modules);
        allStates.finalProject[1](prefillData.finalProject);
        allStates.mappings[1](prefillData.mappings);

        if (wizardActiveStep > 2) {
            setWizardActiveStep(2);
        }

        setSnackbarSeverity("success");
        setSnackbarMessage(t("prefill_success"));
        setSnackbarOpen(true);
    }

    const prefill = (bachelorSubject: string | undefined) => {
        if (bachelorSubject === CS_KEY) {
            prefillWith(prefillCs);
        } else if (bachelorSubject === DS_KEY) {
            prefillWith(prefillDs);
        } else if (bachelorSubject === BI_KEY) {
            prefillWith(prefillBi);
        } else {
            setSnackbarSeverity("warning");
            setSnackbarMessage(t("prefill_failure"));
            setSnackbarOpen(true);
            return;
        }
    }

    const theme = createTheme({})

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const updateSize = () => {
        setWindowHeight(window.innerHeight);
        setWindowWidth(window.innerWidth);
    }

    const FORM_ID = `${FORM_NAME} | ${FORM_VERSION} | ${FORM_DATE}`;

    const allInternalState = () => {
        const allInternalState = {
            formId: FORM_ID,
            base: allStates.base[0],
            languages: allStates.languages[0],
            degrees: allStates.degrees[0],
            modules: allStates.modules[0],
            finalProject: allStates.finalProject[0],
            mappings: allStates.mappings[0],
            comment: allStates.comment[0],
            wizardActiveStep: wizardActiveStep,
            startTime: allStates.startTime[0],
            resetConfirmationOpen: resetConfirmationOpen,
            prefillConfirmationOpen: prefillConfirmationOpen,
            lang: lang,
            snackbarOpen: snackbarOpen,
            snackbarSeverity: snackbarSeverity,
            snackbarMessage: snackbarMessage
        }
        return JSON.stringify(allInternalState, null, "    ");
    }

    const setAllInternalState = (newState : {
        formId: string,
        base: Base,
        languages: Languages,
        degrees: Degrees,
        modules: ModuleList[],
        finalProject: FinalProject,
        mappings: Mapping[],
        comment: Comment,
        wizardActiveStep: number,
        startTime: number,
        resetConfirmationOpen: boolean,
        prefillConfirmationOpen: boolean,
        lang: string,
        snackbarOpen: boolean,
        snackbarSeverity: "info" | "success" | "warning" | "error",
        snackbarMessage: string
    }) => {
        if (newState.formId !== FORM_ID) {
            console.warn("Form ID does not match. Expected: " + FORM_ID + " but was: " + newState.formId);
        }
        if (newState.lang !== lang) {
            console.warn("Language does not match. Expected: " + lang + " but was: " + newState.lang);
        }
        allStates.base[1](newState.base);
        allStates.languages[1](newState.languages);
        allStates.degrees[1](newState.degrees);
        allStates.modules[1](newState.modules);
        allStates.finalProject[1](newState.finalProject);
        allStates.mappings[1](newState.mappings);
        allStates.comment[1](newState.comment);
        setWizardActiveStep(newState.wizardActiveStep);
        allStates.startTime[1](newState.startTime);
        setResetConfirmationOpen(newState.resetConfirmationOpen);
        setPrefillConfirmationOpen(newState.prefillConfirmationOpen);
        setSnackbarOpen(newState.snackbarOpen);
        setSnackbarSeverity(newState.snackbarSeverity);
        setSnackbarMessage(newState.snackbarMessage);
    }
    window.addEventListener("resize", updateSize);

    const runningApplicationBody =
        <React.Fragment>
            <Box sx={{flexGrow: 1}}>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <Paper sx={{p: 1}} elevation={3}>
                            <Stack spacing={2}>
                                <Alert severity="info">
                                    <FromMarkdown>{t("info.markdown")}</FromMarkdown>
                                </Alert>
                                <Alert severity="warning">
                                    <FromMarkdown>{t("disclaimer.markdown")}</FromMarkdown>
                                </Alert>
                            </Stack>
                        </Paper>
                        <Stack spacing={2}>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={() => {
                                    if (lang === 'en') {
                                        window.open("?lang=de", "_self");
                                    } else { // lang === 'de'
                                        window.open("?lang=en", "_self");
                                    }
                                }}
                            >
                                {t("switch_language")}
                            </Button>
                            {/*<Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => window.open(lang + "/faq.html")}
                                >
                                    {t("help")}
                                </Button>*/}
                            <Box sx={{display: 'flex', flexDirection: 'row'}}>
                                <Card sx={{width: "100%", padding: 1}} elevation={3}>
                                    <CardActionArea
                                        href={tolgee.getLanguage() === "de" ? "video/marcia-subs_de.mp4" : "video/marcia-subs_en.mp4"}
                                        target="_blank">
                                        <CardMedia
                                            component="img"
                                            image="video.png"
                                            alt="instructional video"
                                        />
                                        <CardContent>
                                            <Typography variant="body2" sx={{color: 'text.secondary'}}>
                                                {t("watch_video")}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Box>
                        </Stack>
                    </Stack>
                    <Grid container spacing={2}>
                        <Grid size="grow">
                            <Stack spacing={2} direction="column">
                                <Box sx={{flexGrow: 1}}>
                                    <AppBar position="sticky">
                                        <Toolbar>
                                            <Typography variant="h6" component="div" sx={{flexGrow: 1}}>
                                                {t("form_title")}
                                            </Typography>
                                            <ButtonGroup variant="text" sx={{justifyContent: "space-between"}}
                                                         orientation={windowWidth < SMALL_WINDOW_WIDTH ? "vertical" : "horizontal"}>

                                                <Button color="inherit"
                                                        onClick={resetShowDialogs}>
                                                    {t("reset_dialogs")}
                                                </Button>
                                                <Button color="inherit"
                                                        onClick={() => setResetConfirmationOpen(true)}>
                                                    {t("reset")}
                                                </Button>
                                                <ConfirmationDialog
                                                    dialogOpenState={[resetConfirmationOpen, setResetConfirmationOpen]}
                                                    titleKey="reset_confirm_dialog_title"
                                                    bodyKey="reset_confirm_dialog_text"
                                                    action={reset}/>
                                                <Button color="inherit"
                                                        onClick={() => {
                                                            setPrefillConfirmationOpen(true)
                                                        }}>
                                                    {t("pre_fill")}
                                                </Button>
                                                <MarburgBachelorDialog
                                                    dialogOpenState={[prefillConfirmationOpen, setPrefillConfirmationOpen]}
                                                    action={prefill}/>
                                                <Button color="inherit"
                                                        //The finish button should never be disabled in order to not prevent anybody from downloading the required form.
                                                        // disabled={isPreviousStepsIncomplete(stepForms.length, stepForms, allStates, t)}
                                                        onClick={() => {
                                                            setWizardActiveStep(stepForms.length)
                                                        }}>
                                                    {t("finish")}
                                                </Button>
                                            </ButtonGroup>
                                        </Toolbar>
                                    </AppBar>
                                    <Toolbar id="back-to-top-anchor"/>
                                    <Container>
                                        <Wizard
                                            stepForms={stepForms}
                                            allStates={allStates}
                                            activeStepState={[wizardActiveStep, setWizardActiveStep]}
                                            windowWidth={windowWidth}
                                        />
                                    </Container>
                                    <ScrollTop>
                                        <Fab size="small" aria-label="scroll back to top">
                                            <KeyboardArrowUpIcon/>
                                        </Fab>
                                    </ScrollTop>
                                    {getDebug() ?
                                        <Box
                                            role="presentation"
                                            sx={{ position: 'fixed', bottom: 16, left: 16 }}
                                        >
                                            <Fab size="small" aria-label={t("aria.scroll_to_top")} onClick={() => {
                                                if (tolgee.getLanguage() === "de")
                                                    tolgee.changeLanguage("en")
                                                else
                                                    tolgee.changeLanguage("de");
                                            }}>
                                                <TranslateIcon />
                                            </Fab>
                                        </Box>
                                        :
                                        <React.Fragment></React.Fragment>
                                    }
                                </Box>
                            </Stack>
                        </Grid>
                        <Grid size={3}>
                            <ErrorBoundary fallback={
                                <React.Fragment>
                                    <Typography>The Dashboard is not available.</Typography>
                                </React.Fragment>}
                            >
                                <AppBar position="sticky" color="transparent" elevation={0}>
                                    <Box
                                        sx={{
                                            mb: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            maxHeight: windowHeight,
                                            overflow: "hidden",
                                            overflowY: "scroll",
                                            overflowX: "scroll",
                                        }}
                                    >
                                        <Dashboard
                                            allStates={allStates}
                                            stepForms={stepForms}
                                        />
                                    </Box>
                                </AppBar>
                            </ErrorBoundary>
                        </Grid>
                    </Grid>
                    <Divider variant="middle" />
                    <Stack spacing={2} direction="row">
                        {testMode ?
                            <React.Fragment>
                                <Button
                                    onClick={() => {
                                        let result = "{";
                                        result += Array.from(missingKeys.keys()).map(lang => {
                                            return (
                                                "\"" + lang + "\": {" +
                                                Array.from(missingKeys.get(lang)?.entries() ?? []).sort((a, b) => a[0].localeCompare(b[0])).map(entry => {
                                                    return JSON.stringify(entry[0]) + ": " + JSON.stringify(entry[1]);
                                                }).join(",\n") +
                                                "}");
                                        }).join(",\n");
                                        result += "\n}";
                                        // navigator.clipboard.writeText(result);

                                        var element = document.createElement('a');
                                        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(result));
                                        element.setAttribute('download', "MissingTranslations.json");
                                        element.style.display = 'none';
                                        document.body.appendChild(element);
                                        element.click();
                                        document.body.removeChild(element);

                                    }}
                                >Download Missing I18n-Keys</Button>
                                <Box sx={{flexGrow: 1}}></Box>
                            </React.Fragment> :
                            <React.Fragment/>
                        }

                        <Button
                            onClick={() => {
                                setReportErrorDialogOpen(true);
                            }}
                        >{t("error_reports")}</Button>
                        <Dialog
                            open={reportErrorDialogOpen}
                            onClose={() => setReportErrorDialogOpen(false)}
                        >
                            <DialogTitle id="repoort-error-dialog">
                                {t("report_error_dialog_title")}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText id="report-error-dialog-description">
                                    <FromMarkdown>{t("report_error_dialog_body")}</FromMarkdown>
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => {
                                    const text = allInternalState();
                                    var element = document.createElement('a');
                                    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
                                    element.setAttribute('download', "MarciaState.json");
                                    element.style.display = 'none';
                                    document.body.appendChild(element);
                                    element.click();
                                    document.body.removeChild(element);
                                }}>{t("download_state")}</Button>
                                <Button onClick={() => setReportErrorDialogOpen(false)}>{t("close")}</Button>
                            </DialogActions>
                        </Dialog>
                        <Box sx={{flexGrow: 1}}></Box>
                        <Button target="_blank" href={tolgee.unwrap(t("data_protection_declaration_url")).text}>{t("data_protection_declaration")}</Button>
                        <Box sx={{flexGrow: 1}}></Box>
                        <Button target="_blank" href={tolgee.unwrap(t("imprint_url")).text}>{t("imprint")}</Button>
                        {
                            debug ?
                                <React.Fragment>
                                    <Box sx={{flexGrow: 1}}></Box>
                                    <TextField
                                        id="debugInput"
                                        label="Debug"
                                        value={debugText}
                                        onChange={(event) => {
                                            setDebugText(event.target.value);
                                        }}
                                    />
                                    <Button onClick={() => {setAllInternalState(JSON.parse(debugText));}}>Load</Button>
                                </React.Fragment> :
                                <React.Fragment />

                        }
                        <Box sx={{flexGrow: 1}}></Box>
                        <Typography variant="body2">{FORM_ID}</Typography>
                    </Stack>

                </Stack>
            </Box>
            <Snackbar open={snackbarOpen}
                      autoHideDuration={6000}
                      onClose={(
                          event: React.SyntheticEvent | Event,
                          reason?: SnackbarCloseReason,
                      ) => {
                          if (reason === 'clickaway') {
                              return;
                          }

                          setSnackbarOpen(false);
                      }}
            >
                <Alert
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{width: '100%'}}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </React.Fragment>;

    const [modalOpen, ] = React.useState(Configuration.maintenance);

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    return (
        <ThemeProvider theme={theme}>
            <Typography variant="h3" gutterBottom>
                {t("title")}
            </Typography>
            {runningApplicationBody}
            <Modal
                open={modalOpen}
            >
                <Box sx={modalStyle}>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        Maintenance
                    </Typography>
                    <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                        This web form is down for maintenance. It will be activated in due time before the application deadline starts, at the latest on May 1, 2026.
                    </Typography>
                </Box>
            </Modal>
        </ThemeProvider>
    );
}

export default App;
